"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faPhone, faRoute, faUsers, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useToast } from "@/components/ui/ToastContainer";
import "@/styles/customer-call.css";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://app.canerozen.com/api";

type ModuleInfo = {
  id: number;
  name: string;
  title: string;
  subtitle: string;
  introTitle: string;
  introText: string;
  features: { title: string; text: string }[];
  videoUrl: string;
  faqs: { question: string; answer: string }[];
};

type ModuleInfoDefaults = Partial<Omit<ModuleInfo, "id" | "name">> & {
  title?: string;
  name?: string;
};

type ModuleInfoModalProps = {
  open: boolean;
  moduleId: number;
  onClose: () => void;
  source?: string;
  metadata?: Record<string, unknown>;
  defaults?: ModuleInfoDefaults;
  eyebrow?: string;
};

function getToken() {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("auth_token") || "";
}

export function ModuleInfoModal({
  open,
  moduleId,
  onClose,
  source = "module_unavailable_modal",
  metadata = {},
  defaults,
  eyebrow = "Farktor Modül",
}: ModuleInfoModalProps) {
  const { showToast } = useToast();
  const [moduleInfo, setModuleInfo] = useState<ModuleInfo | null>(null);
  const [requestSaving, setRequestSaving] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [videoOpen, setVideoOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRequestMessage("");
    setVideoOpen(false);
    setFaqOpen(false);

    async function loadModuleInfo() {
      try {
        const response = await fetch(`${API_BASE}/call-center/module-info/${moduleId}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
          cache: "no-store",
        });
        const json = await response.json().catch(() => ({}));
        if (!cancelled && response.ok && json?.success !== false) setModuleInfo(json.data || null);
      } catch {
        if (!cancelled) setModuleInfo(null);
      }
    }

    loadModuleInfo();
    return () => {
      cancelled = true;
    };
  }, [moduleId, open]);

  const content = useMemo(() => {
    const fallbackFeatures = defaults?.features?.length ? defaults.features : [
      { title: "Modül aktivasyonu", text: "Bu işlemi kullanabilmek için ilgili modül mağazanızda aktif olmalıdır." },
      { title: "Operasyon akışı", text: "Aktif olduğunda süreç panel içinden kayıtlı ve takip edilebilir şekilde ilerler." },
      { title: "Bilgi talebi", text: "Talebiniz Farktor ekibine iletilir, modül kurulumu için dönüş yapılır." },
    ];
    return {
      title: moduleInfo?.title || defaults?.title || defaults?.name || "Modül Bilgisi",
      introTitle: moduleInfo?.introTitle || defaults?.introTitle || defaults?.title || "Modül aktivasyonu gerekir",
      introText: moduleInfo?.introText || defaults?.introText || "Bu işlemi kullanabilmek için ilgili modülün mağazanızda aktif olması gerekir.",
      features: moduleInfo?.features?.length ? moduleInfo.features : fallbackFeatures,
      videoUrl: moduleInfo?.videoUrl || defaults?.videoUrl || "",
      faqs: moduleInfo?.faqs?.length ? moduleInfo.faqs : defaults?.faqs || [],
    };
  }, [defaults, moduleInfo]);

  const submitInfoRequest = useCallback(async () => {
    if (requestSaving) return;
    setRequestSaving(true);
    setRequestMessage("");
    try {
      const response = await fetch(`${API_BASE}/call-center/module-info/${moduleId}/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          source,
          path: typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "",
          metadata: {
            module: `mod_${moduleId}`,
            title: content.title,
            ...metadata,
          },
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) throw new Error(json?.message || "Bilgi talebi alınamadı");
      const message = json?.message || "Bilgi talebiniz alındı.";
      setRequestMessage(message);
      showToast(message, "success", 3000);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Bilgi talebi alınamadı";
      setRequestMessage(message);
      showToast(message, "error", 3000);
    } finally {
      setRequestSaving(false);
    }
  }, [content.title, metadata, moduleId, requestSaving, showToast, source]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (videoOpen) {
        setVideoOpen(false);
        return;
      }
      if (faqOpen) {
        setFaqOpen(false);
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [faqOpen, onClose, open, videoOpen]);

  if (!open) return null;

  const hasVideo = Boolean(content.videoUrl);
  const hasFaqs = Boolean(content.faqs?.length);
  const modal = (
    <div className="customer-call-service" role="dialog" aria-modal="true" aria-label={content.title}>
      <div className="customer-call-service__panel">
        <div className="customer-call-service__header">
          <div>
            <span>{eyebrow}</span>
            <strong>{content.title}</strong>
          </div>
          <button type="button" onClick={onClose} aria-label="Kapat">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="customer-call-service__hero">
          <div className="customer-call-service__icon">
            <FontAwesomeIcon icon={faPhone} />
          </div>
          <div>
            <strong>{content.introTitle}</strong>
            <p>{content.introText}</p>
          </div>
        </div>

        <div className="customer-call-service__grid">
          {content.features.slice(0, 3).map((feature, index) => (
            <div key={`${feature.title}-${index}`}>
              <FontAwesomeIcon icon={index === 0 ? faUsers : index === 1 ? faRoute : faCircleInfo} />
              <strong>{feature.title}</strong>
              <span>{feature.text}</span>
            </div>
          ))}
        </div>

        {(hasVideo || hasFaqs) && (
          <div className="customer-call-service__links">
            {hasVideo && <button type="button" onClick={() => setVideoOpen(true)}>Videoyu İzle</button>}
            {hasFaqs && <button type="button" onClick={() => setFaqOpen(true)}>Sık Sorulan Sorular</button>}
          </div>
        )}

        <div className="customer-call-service__footer">
          <span>Bu hizmet mağazanızda bulunmamaktadır. Sistem yöneticiniz ile iletişime geçebilirsiniz.</span>
          <button type="button" onClick={submitInfoRequest} disabled={requestSaving}>
            {requestSaving ? "Gönderiliyor" : "Detaylı Bilgi Talep Et"}
          </button>
        </div>
        {requestMessage && <div className="customer-call-service__feedback">{requestMessage}</div>}
      </div>

      {videoOpen && content.videoUrl && (
        <div className="customer-call-service__submodal" role="dialog" aria-modal="true">
          <div className="customer-call-service__subpanel">
            <div className="customer-call-service__subheader">
              <strong>Video</strong>
              <button type="button" onClick={() => setVideoOpen(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <iframe src={content.videoUrl} title={`${content.title} video`} allow="autoplay; fullscreen; picture-in-picture" />
          </div>
        </div>
      )}

      {faqOpen && !!content.faqs?.length && (
        <div className="customer-call-service__submodal" role="dialog" aria-modal="true">
          <div className="customer-call-service__subpanel">
            <div className="customer-call-service__subheader">
              <strong>Sık Sorulan Sorular</strong>
              <button type="button" onClick={() => setFaqOpen(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div className="customer-call-service__faq">
              {content.faqs.map((faq, index) => (
                <details key={`${faq.question}-${index}`}>
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}
